import { useCallback, useEffect, useState } from 'react';
import { crmApi } from '@/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { DataTable } from '@/components/ui/DataTable';
import { dataTablePageProps, dataTableSearchProps, usePagination } from '@/hooks/usePagination';
import { useDebounce } from '@/hooks/useDebounce';
import type { CrmCompany } from '@/types/crm';
import { showApiError } from '@/utils/apiError';

export function CrmCompaniesPage() {
  const pagination = usePagination();
  const debouncedSearch = useDebounce(pagination.search);
  const [rows, setRows] = useState<CrmCompany[]>([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await crmApi.listCompanies({
        page: pagination.page,
        size: pagination.size,
        search: debouncedSearch || undefined,
      });
      setRows(res.content);
      pagination.applyPageResponse(res);
    } catch (err) {
      showApiError(err, 'Failed to load companies');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.size, debouncedSearch, pagination]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div>
      <PageHeader
        title="Companies"
        description="B2B accounts with contacts, deals, and activities"
        breadcrumbs={[{ label: 'CRM', to: '/crm' }, { label: 'Companies' }]}
        actions={
          <Button
            onClick={() =>
              crmApi
                .createCompany({ name: `Company ${Date.now()}` })
                .then(load)
                .catch((e) => showApiError(e, 'Create failed'))
            }
          >
            Add company
          </Button>
        }
      />
      <DataTable
        keyField="id"
        columns={[
          { key: 'name', header: 'Name' },
          { key: 'industry', header: 'Industry' },
          { key: 'email', header: 'Email' },
          { key: 'phone', header: 'Phone' },
        ]}
        data={rows}
        loading={loading}
        {...dataTableSearchProps(pagination)}
        {...dataTablePageProps(pagination)}
      />
    </div>
  );
}
